<template>
  <div class="h-screen flex w-full bg-img vx-row no-gutter items-center justify-center" id="page-login">
    <div class="vx-col sm:w-1/2 md:w-1/2 lg:w-3/4 xl:w-2/3 sm:m-0 m-4">
      <vx-card>
        <div slot="no-body" class="full-page-bg-color bg-wite">

          <div class="vx-row no-gutter justify-center items-stretch">
            <div class="vx-col hidden lg:flex lg:w-1/2 px-4 py-10 color-bg-login min-h-login flex-col justify-center items-center">
              <img class="mb-5 max-w-logo block mx-auto" src="@/assets/images/logo/logo-full.svg" alt="registerImg">
              <img src="@/assets/images/pages/workspace.svg" alt="login" class="questions-illustration mx-auto">
            </div>

            <div class="vx-col sm:w-full md:w-full lg:w-1/2 d-theme-dark-bg">
              <div class="p-8 login-tabs-container py-login">

                <div class="vx-row vx-card__title mb-2">
                  <img class="block lg:hidden mb-5 max-w-logo mx-auto" src="@/assets/images/logo/logo-full.svg" alt="registerImg">

                  <div class="vx-col md:w-full justify-center items-center">
                    <h2 class="mb-1">¡Queremos conocerte!</h2>
                    <p class="step-subtitle">{{ stepSubtitle }}</p>
                    <p class="step-meta">Paso {{ step }} de {{ totalSteps }} · {{ estimatedTime }}</p>

                    <!-- Stepper visual: chips numeradas + barra de progreso -->
                    <div class="stepper" :aria-label="`Paso ${step} de ${totalSteps}`">
                      <div v-for="n in totalSteps" :key="`step-${n}`" class="stepper-segment">
                        <div
                          class="stepper-chip"
                          :class="{ done: n < step, current: n === step }"
                          @click="goToStep(n)"
                        >
                          <feather-icon v-if="n < step" icon="CheckIcon" svgClasses="w-3 h-3" />
                          <span v-else>{{ n }}</span>
                        </div>
                        <div
                          v-if="n < totalSteps"
                          class="stepper-bar"
                          :class="{ done: n < step }"
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>

                <div v-if="loading" class="vx-row">
                  <div class="vx-col w-full text-center py-10">
                    <p class="text-sm text-gray-500">Cargando...</p>
                  </div>
                </div>

                <transition :name="stepTransitionName" mode="out-in">
                <div v-if="!loading" :key="step" class="vx-row step-content">
                  <!-- ============================================ -->
                  <!-- Caso A — Step 1: "Sobre tu empresa"           -->
                  <!-- industry + company_size + primary_goal        -->
                  <!-- ============================================ -->
                  <template v-if="!skipPhase1 && step === 1">
                    <div class="vx-col w-full" :class="{ 'has-error': errors.company_industry }">
                      <label class="text-sm label-vs block">¿Cuál es tu sector o enfoque?</label>
                      <v-select
                        v-model="form.company_industry"
                        :options="optCompanyIndustry"
                        :searchable="false"
                        class="select-large mt-2 w-full"
                        placeholder="Selecciona una opción"
                      ></v-select>
                      <span v-if="errors.company_industry" class="error-text">{{ errors.company_industry }}</span>
                    </div>
                    <div class="vx-col w-full" :class="{ 'has-error': errors.company_size }">
                      <label class="text-sm label-vs block mt-3">Tamaño de la empresa</label>
                      <v-select
                        v-model="form.company_size"
                        :options="optCompanySize"
                        :searchable="false"
                        class="select-large mt-2 w-full"
                        placeholder="Selecciona una opción"
                      ></v-select>
                      <span v-if="errors.company_size" class="error-text">{{ errors.company_size }}</span>
                    </div>
                    <div class="vx-col w-full" :class="{ 'has-error': errors.primary_goal }">
                      <label class="text-sm label-vs block mt-3">¿Qué es lo primero que necesitas lograr hoy?</label>
                      <v-select
                        v-model="form.primary_goal"
                        :options="optPrimaryGoal"
                        :searchable="false"
                        class="select-large mt-2 w-full"
                        placeholder="Selecciona una opción"
                      ></v-select>
                      <span v-if="errors.primary_goal" class="error-text">{{ errors.primary_goal }}</span>
                    </div>
                  </template>

                  <!-- ============================================ -->
                  <!-- Caso B — Step 1: primary_goal solo            -->
                  <!-- (resto viene pre-hidratado desde HubSpot)     -->
                  <!-- ============================================ -->
                  <template v-if="skipPhase1 && step === 1">
                    <div class="vx-col w-full" :class="{ 'has-error': errors.primary_goal }">
                      <label class="text-sm label-vs block">¿Qué es lo primero que necesitas lograr hoy?</label>
                      <v-select
                        v-model="form.primary_goal"
                        :options="optPrimaryGoal"
                        :searchable="false"
                        class="select-large mt-2 w-full"
                        placeholder="Selecciona una opción"
                      ></v-select>
                      <span v-if="errors.primary_goal" class="error-text">{{ errors.primary_goal }}</span>
                    </div>
                  </template>

                  <!-- ============================================ -->
                  <!-- Caso A — Step 2: "Tus necesidades"            -->
                  <!-- services + implementation_timeline + volume   -->
                  <!-- WHY: monthly_volume al final del paso 2 para  -->
                  <!-- que la carga cognitiva (cálculo) caiga cuando -->
                  <!-- el usuario ya tiene momentum y es menos       -->
                  <!-- probable que abandone.                        -->
                  <!-- ============================================ -->
                  <template v-if="!skipPhase1 && step === 2">
                    <div class="vx-col w-full" :class="{ 'has-error': errors.services }">
                      <label class="text-sm label-vs block">Servicios de interés (puedes elegir varios)</label>
                      <v-select
                        v-model="form.services"
                        :options="availableServices"
                        :searchable="false"
                        :close-on-select="false"
                        multiple
                        class="select-large services-multiselect mt-2 w-full"
                        placeholder="Selecciona uno o más"
                      >
                        <template #no-options>
                          <span class="text-sm text-gray-500">Ya seleccionaste todos los servicios.</span>
                        </template>
                      </v-select>
                      <span v-if="errors.services" class="error-text">{{ errors.services }}</span>
                    </div>
                    <div class="vx-col w-full" :class="{ 'has-error': errors.implementation_timeline }">
                      <label class="text-sm label-vs block mt-3">¿Qué tan rápido esperas integrar nuestra solución de firma?</label>
                      <v-select
                        v-model="form.implementation_timeline"
                        :options="optImplementationTimeline"
                        :searchable="false"
                        class="select-large mt-2 w-full"
                        placeholder="Selecciona una opción"
                      ></v-select>
                      <span v-if="errors.implementation_timeline" class="error-text">{{ errors.implementation_timeline }}</span>
                    </div>
                    <div class="vx-col w-full" :class="{ 'has-error': errors.monthly_volume }">
                      <label class="text-sm label-vs block mt-3">¿Cuántas firmas estimas realizar al mes?	</label>
                      <v-select
                        v-model="form.monthly_volume"
                        :options="optMonthlyVolume"
                        :searchable="false"
                        class="select-large mt-2 w-full"
                        placeholder="Selecciona una opción"
                      ></v-select>
                      <span v-if="errors.monthly_volume" class="error-text">{{ errors.monthly_volume }}</span>
                    </div>
                  </template>

                  <!-- ============================================ -->
                  <!-- Final step — success_indicator + source +     -->
                  <!-- use_case + contact_agent + preferred_method   -->
                  <!-- ============================================ -->
                  <template v-if="step === totalSteps">
                    <div class="vx-col w-full" :class="{ 'has-error': errors.success_indicator }">
                      <label class="text-sm label-vs block">¿Qué es lo más importante que esperas resolver con nosotros?</label>
                      <v-select
                        v-model="form.success_indicator"
                        :options="optSuccessIndicator"
                        :searchable="false"
                        class="select-large mt-2 w-full"
                        placeholder="Selecciona una opción"
                      ></v-select>
                      <span v-if="errors.success_indicator" class="error-text">{{ errors.success_indicator }}</span>
                    </div>
                    <div class="vx-col w-full" :class="{ 'has-error': errors.source }">
                      <label class="text-sm label-vs block mt-3">¿Cómo se enteró de nosotros?</label>
                      <v-select
                        v-model="form.source"
                        :options="optSource"
                        :searchable="false"
                        class="select-large mt-2 w-full"
                        placeholder="Selecciona una opción"
                      ></v-select>
                      <span v-if="errors.source" class="error-text">{{ errors.source }}</span>
                    </div>
                    <div class="vx-col w-full" :class="{ 'has-error': errors.use_case }">
                      <label class="text-sm label-vs block mt-3">Cuéntanos tu problema #1 que necesitas resolver</label>
                      <textarea
                        v-model="form.use_case"
                        rows="3"
                        maxlength="1000"
                        class="vs-input--input mt-2 w-full"
                        placeholder="Describe brevemente el caso de uso o el dolor que quieres resolver"
                      ></textarea>
                      <div class="flex justify-between items-start mt-1">
                        <span v-if="errors.use_case" class="error-text">{{ errors.use_case }}</span>
                        <span v-else></span>
                        <span class="char-counter" :class="{ 'char-counter--warn': useCaseCharCount > 900 }">
                          {{ useCaseCharCount }} / 1000
                        </span>
                      </div>
                    </div>
                    <div class="vx-col w-full" :class="{ 'has-error': errors.contact_agent }">
                      <label class="text-sm label-vs block mt-3">¿Te gustaría que un asesor te contacte?</label>
                      <v-select
                        v-model="form.contact_agent"
                        :options="optContactAgent"
                        :reduce="opt => opt.value"
                        :searchable="false"
                        class="select-large mt-2 w-full"
                        placeholder="Selecciona una opción"
                      ></v-select>
                      <span v-if="errors.contact_agent" class="error-text">{{ errors.contact_agent }}</span>
                    </div>
                    <div v-if="form.contact_agent === contactAgentYes" class="vx-col w-full" :class="{ 'has-error': errors.preferred_contact_method }">
                      <label class="text-sm label-vs block mt-3">¿Por qué medio prefieres que te contactemos?</label>
                      <v-select
                        v-model="form.preferred_contact_method"
                        :options="optContactMethod"
                        :reduce="opt => opt.value"
                        :searchable="false"
                        class="select-large mt-2 w-full"
                        placeholder="Selecciona una opción"
                      ></v-select>
                      <span v-if="errors.preferred_contact_method" class="error-text">{{ errors.preferred_contact_method }}</span>
                    </div>
                  </template>

                  <!-- Navigation buttons -->
                  <div class="vx-col nav-buttons mt-5 w-full flex justify-between">
                    <vs-button
                      v-if="step > 1"
                      type="border"
                      color="primary"
                      size="large"
                      icon-pack="feather"
                      icon="icon-arrow-left"
                      @click="prevStep">Anterior</vs-button>
                    <span v-else></span>
                    <vs-button
                      v-if="step < totalSteps"
                      id="button-with-loading"
                      class="ml-auto"
                      size="large"
                      icon-pack="feather"
                      icon="icon-arrow-right"
                      icon-after
                      @click="nextStep">Siguiente</vs-button>
                    <vs-button
                      v-else
                      id="button-with-loading"
                      class="ml-auto"
                      size="large"
                      icon-pack="feather"
                      icon="icon-check"
                      icon-after
                      :disabled="isSubmitting"
                      @click="submit">Finalizar</vs-button>
                  </div>
                </div>
                </transition>
              </div>

            </div>

          </div>
        </div>
      </vx-card>
    </div>

    <vs-popup class="holamundo" title="Agendar llamada" :active.sync="showHubspotModal" @close="clearHubspotEmbed">
      <div ref="hubspotContainer" style="min-height: 500px;"></div>
    </vs-popup>
  </div>
</template>

<script>
import vSelect from 'vue-select';
import router from '@/router';
import {
  COMPANY_INDUSTRY,
  SERVICES,
  IMPLEMENTATION_TIMELINE,
  COMPANY_SIZE,
  MONTHLY_VOLUME,
  PRIMARY_GOAL,
  SUCCESS_INDICATOR,
  SOURCE_OPTIONS,
  PREFERRED_CONTACT_METHOD,
  CONTACT_AGENT_YES,
  CONTACT_AGENT_NO,
} from './options';

// Key for sessionStorage persistence. WHY: si el usuario refresca o vuelve por
// el navegador, no se pierde el progreso del cuestionario (form + step).
const STORAGE_KEY = 'vmx_onboarding_draft_v1';

// User-facing error messages — kept here for centralization.
const ERR = {
  company_industry:        'Selecciona tu sector.',
  services:                'Selecciona al menos un servicio.',
  implementation_timeline: 'Indica el plazo estimado de integración.',
  company_size:            'Selecciona el tamaño de tu empresa.',
  monthly_volume:          'Selecciona el volumen mensual de firmas.',
  primary_goal:            'Selecciona qué quieres lograr hoy.',
  success_indicator:       'Selecciona tu principal indicador de éxito.',
  source:                  'Indica cómo te enteraste de nosotros.',
  use_case:                'Cuéntanos tu problema #1.',
  contact_agent:           'Indica si deseas que un asesor te contacte.',
  preferred_contact_method:'Selecciona el medio de contacto preferido.',
};

export default {
  components: {
    'v-select': vSelect,
  },
  data() {
    return {
      loading: true,
      isSubmitting: false,
      skipPhase1: false,
      step: 1,
      // Dirección de la transición entre pasos. 'forward' = sale ← / entra →.
      // 'backward' = sale → / entra ← (al apretar "Anterior" o un chip done).
      stepDirection: 'forward',
      showHubspotModal: false,

      // Expuesto al template para evitar strings hardcoded en v-if.
      contactAgentYes: CONTACT_AGENT_YES,

      // Catalogs (single source of truth — see options.js)
      optCompanyIndustry: COMPANY_INDUSTRY,
      optServices: SERVICES,
      optImplementationTimeline: IMPLEMENTATION_TIMELINE,
      optCompanySize: COMPANY_SIZE,
      optMonthlyVolume: MONTHLY_VOLUME,
      optPrimaryGoal: PRIMARY_GOAL,
      optSuccessIndicator: SUCCESS_INDICATOR,
      optSource: SOURCE_OPTIONS,
      optContactAgent: [
        { value: CONTACT_AGENT_YES, label: 'Sí' },
        { value: CONTACT_AGENT_NO,  label: 'No' },
      ],
      optContactMethod: PREFERRED_CONTACT_METHOD,

      form: {
        company_industry: null,
        services: [],
        implementation_timeline: null,
        company_size: null,
        monthly_volume: null,
        primary_goal: null,
        success_indicator: null,
        use_case: '',
        source: null,
        contact_agent: null,
        preferred_contact_method: null,
      },

      // Error map — pre-declared so Vue 2 makes every key reactive without $set.
      // Empty string = no error; non-empty string = message shown next to field.
      errors: {
        company_industry: '',
        services: '',
        implementation_timeline: '',
        company_size: '',
        monthly_volume: '',
        primary_goal: '',
        success_indicator: '',
        use_case: '',
        source: '',
        contact_agent: '',
        preferred_contact_method: '',
      },
    };
  },
  computed: {
    totalSteps() {
      return this.skipPhase1 ? 2 : 3;
    },
    stepTransitionName() {
      return this.stepDirection === 'backward' ? 'step-slide-back' : 'step-slide';
    },
    stepSubtitle() {
      const A = ['Sobre tu empresa', 'Tus necesidades', 'Casi listo'];
      const B = ['Tu objetivo principal', 'Casi listo'];
      const map = this.skipPhase1 ? B : A;
      return map[this.step - 1] || '';
    },
    estimatedTime() {
      return this.skipPhase1 ? '20 segundos' : '40 segundos';
    },
    useCaseCharCount() {
      return (this.form.use_case || '').length;
    },
    /**
     * Hides services already chosen so the dropdown stops showing duplicates.
     * If the user picked "Todas las anteriores", the watcher auto-fills every
     * option, leaving this list empty → the slot's "Ya seleccionaste todos"
     * message kicks in.
     */
    availableServices() {
      const selected = this.form.services || [];
      return this.optServices.filter(s => !selected.includes(s));
    },
  },
  watch: {
    'form.preferred_contact_method'(newValue) {
      if (newValue === 'llamada') {
        this.showHubspotModal = true;
      }
      if (newValue) this.errors.preferred_contact_method = '';
    },
    'form.contact_agent'(newValue) {
      if (newValue === CONTACT_AGENT_NO) {
        this.form.preferred_contact_method = null;
        this.errors.preferred_contact_method = '';
      }
      if (newValue) this.errors.contact_agent = '';
    },
    'form.services'(newValue, oldValue) {
      // WHY: cuando el usuario marca "Todas las anteriores", auto-seleccionar
      // visualmente todas las demás opciones; al deseleccionarla, vaciar todo.
      // El payload final filtra esto en submit() para mandar solo
      // ["Todas las anteriores"] al backend (evita inflar score).
      const ALL = 'Todas las anteriores';
      const newArr = newValue || [];
      const oldArr = oldValue || [];
      const justAdded   = newArr.includes(ALL) && !oldArr.includes(ALL);
      const justRemoved = !newArr.includes(ALL) && oldArr.includes(ALL);
      if (justAdded && newArr.length !== this.optServices.length) {
        this.form.services = [...this.optServices];
      } else if (justRemoved) {
        this.form.services = [];
      }
      if (newArr.length > 0) this.errors.services = '';
    },
    'form.company_industry'(v)        { if (v) this.errors.company_industry = ''; },
    'form.implementation_timeline'(v) { if (v) this.errors.implementation_timeline = ''; },
    'form.company_size'(v)            { if (v) this.errors.company_size = ''; },
    'form.monthly_volume'(v)          { if (v) this.errors.monthly_volume = ''; },
    'form.primary_goal'(v)            { if (v) this.errors.primary_goal = ''; },
    'form.success_indicator'(v)       { if (v) this.errors.success_indicator = ''; },
    'form.source'(v)                  { if (v) this.errors.source = ''; },
    'form.use_case'(v)                { if (v && v.trim()) this.errors.use_case = ''; },
    showHubspotModal(newValue) {
      if (newValue) {
        this.$nextTick(() => this.injectHubspotEmbed());
      }
    },
    // Persistencia: cualquier cambio en el form o el step se guarda.
    // Deep watch porque form.services es array.
    form: {
      handler() { this.persistDraft(); },
      deep: true,
    },
    step() { this.persistDraft(); },
  },
  mounted() {
    this.loadPrefill();
  },
  methods: {
    loadPrefill() {
      this.$store.dispatch('account/getOnboardingPrefill')
        .then(response => {
          this.skipPhase1 = !!response.skipPhase1;
          if (response.prefillFromHubspot) {
            const p = response.prefillFromHubspot;
            // Keep what HubSpot already knows for transparency / debug; the
            // backend will re-hydrate from HubSpot at submit time anyway.
            if (p.company_industry) this.form.company_industry = p.company_industry;
            if (p.services && p.services.length) this.form.services = p.services;
            if (p.implementation_timeline) this.form.implementation_timeline = p.implementation_timeline;
            if (p.company_size) this.form.company_size = p.company_size;
            if (p.monthly_volume) this.form.monthly_volume = p.monthly_volume;
            if (p.source) this.form.source = p.source;
          }
          // Restaurar draft local DESPUÉS del prefill: si el usuario ya había
          // empezado a llenar y refrescó, su trabajo manual gana sobre HubSpot.
          this.restoreDraft();
          this.loading = false;
        })
        .catch(() => {
          // Fallback: assume Caso A so the user always sees a usable form.
          this.skipPhase1 = false;
          this.restoreDraft();
          this.loading = false;
        });
    },
    persistDraft() {
      if (this.loading) return; // evita guardar el estado inicial vacío
      try {
        const payload = { step: this.step, form: this.form };
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
      } catch (e) { /* sessionStorage lleno o bloqueado — ignorar silenciosamente */ }
    },
    restoreDraft() {
      try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return;
        const saved = JSON.parse(raw);
        if (saved && saved.form) {
          // Merge: solo sobreescribimos campos que el usuario ya había tocado.
          Object.keys(saved.form).forEach(k => {
            const v = saved.form[k];
            const hasValue = Array.isArray(v) ? v.length > 0 : (v !== null && v !== '' && v !== undefined);
            if (hasValue) this.form[k] = v;
          });
        }
        if (saved && typeof saved.step === 'number' && saved.step >= 1 && saved.step <= this.totalSteps) {
          this.step = saved.step;
        }
      } catch (e) { /* JSON corrupto — ignorar */ }
    },
    clearDraft() {
      try { sessionStorage.removeItem(STORAGE_KEY); } catch (e) { /* noop */ }
    },
    notifyMissing(message) {
      this.$vs.notify({
        title: 'Faltan datos',
        text: message,
        color: 'danger',
        position: 'bottom-center',
      });
    },
    /**
     * Validates every field of the current step at once. Marks each missing
     * field individually (red border + error text) instead of bailing on the
     * first one, so the user sees all gaps simultaneously.
     */
    validateStep() {
      const stepFields = this.getStepFields();
      // Reset only the errors of the fields belonging to this step.
      stepFields.forEach(f => { this.errors[f] = ''; });

      const missing = [];
      stepFields.forEach(f => {
        if (this.isFieldMissing(f)) {
          this.errors[f] = ERR[f];
          missing.push(f);
        }
      });

      if (missing.length > 0) {
        this.notifyMissing('Por favor completa los campos marcados en rojo.');
        return false;
      }
      return true;
    },
    getStepFields() {
      // Mirror of the template's step layout. Returns the field names rendered
      // in the current step so we only validate (and only reset) what the user
      // can actually see.
      if (this.skipPhase1 && this.step === 1) return ['primary_goal'];
      if (!this.skipPhase1 && this.step === 1) return ['company_industry', 'company_size', 'primary_goal'];
      if (!this.skipPhase1 && this.step === 2) return ['services', 'implementation_timeline', 'monthly_volume'];
      if (this.step === this.totalSteps) {
        const final = ['success_indicator', 'source', 'use_case', 'contact_agent'];
        if (this.form.contact_agent === CONTACT_AGENT_YES) final.push('preferred_contact_method');
        return final;
      }
      return [];
    },
    isFieldMissing(field) {
      const v = this.form[field];
      if (Array.isArray(v)) return v.length === 0;
      if (typeof v === 'string') return v.trim().length === 0;
      return v === null || v === undefined;
    },
    nextStep() {
      if (!this.validateStep()) {
        this.$nextTick(() => this.scrollToFirstError());
        return;
      }
      if (this.step < this.totalSteps) {
        this.stepDirection = 'forward';
        this.step += 1;
      }
    },
    prevStep() {
      if (this.step > 1) {
        this.stepDirection = 'backward';
        this.step -= 1;
      }
    },
    /**
     * Permite saltar a un step ya completado (chip clicable). No deja avanzar
     * hacia adelante por aquí — para eso está `nextStep` con validación.
     */
    goToStep(n) {
      if (n >= 1 && n < this.step) {
        this.stepDirection = 'backward';
        this.step = n;
      }
    },
    /**
     * Lleva la vista al primer campo con error para que el usuario vea
     * de inmediato qué le falta. Útil sobre todo en mobile.
     */
    scrollToFirstError() {
      const el = document.querySelector('.has-error');
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    },
    submit() {
      if (!this.validateStep()) return;
      this.isSubmitting = true;
      this.$vs.loading({
        background: 'primary',
        color: '#fff',
        container: '#button-with-loading',
        scale: 0.45,
      });

      // WHY: si el usuario marcó "Todas las anteriores" (y por el watcher se
      // ven todas en la UI), al backend solo le mandamos ese único string para
      // que aplique el flat 220 sin riesgo de double-counting.
      const ALL_SERVICES = 'Todas las anteriores';
      const selectedServices = (this.form.services && this.form.services.length)
        ? (this.form.services.includes(ALL_SERVICES) ? [ALL_SERVICES] : this.form.services)
        : null;

      const payload = {
        // Phase 1 — backend will hydrate from HubSpot if missing and prev_score exists.
        company_industry: this.form.company_industry || null,
        services: selectedServices,
        implementation_timeline: this.form.implementation_timeline || null,
        company_size: this.form.company_size || null,
        monthly_volume: this.form.monthly_volume || null,
        // Phase 2 — always sent.
        primary_goal: this.form.primary_goal,
        success_indicator: this.form.success_indicator,
        use_case: this.form.use_case,
        source: this.form.source,
        contact_agent: this.form.contact_agent,
        preferred_contact_method: this.form.contact_agent === CONTACT_AGENT_YES ? this.form.preferred_contact_method : null,
      };

      this.$store.dispatch('account/onBoardingQuestions', { data: payload })
        .then(scoring => {
          this.$store.dispatch('account/me', { aclChangeRole: this.$acl.change }).then(() => {
            this.isSubmitting = false;
            this.$vs.loading.close('#button-with-loading > .con-vs-loading');
            this.clearDraft();
            const cvr = scoring && scoring.cvr_score != null ? scoring.cvr_score : 0;
            const isKo = scoring && scoring.is_ko === true;
            router.push({
              name: 'register-success',
              query: {
                source: 'onboarding',
                cvr_score: String(cvr),
                is_ko: isKo ? '1' : '0',
              },
            });
          });
        })
        .catch(error => {
          this.isSubmitting = false;
          this.$vs.loading.close('#button-with-loading > .con-vs-loading');
          // eslint-disable-next-line no-console
          console.log(error);
        });
    },
    injectHubspotEmbed() {
      const container = this.$refs.hubspotContainer;
      if (!container) return;
      container.innerHTML = '';

      const mexicoCityTime = new Date().toLocaleString("en-US", { timeZone: "America/Mexico_City" });
      const hour = new Date(mexicoCityTime).getHours();
      const baseUrl = hour < 13
        ? 'https://meetings.hubspot.com/danytza?embed=true'
        : 'https://meetings.hubspot.com/mariam-sanchez?embed=true';

      let finalUrl = baseUrl;
      const user = this.$store.state.account.user;
      if (user && user.me) {
        try {
          const me = user.me;
          const email = me.email;
          const firstName = me.name ? me.name.split(' ')[0] : '';
          const lastName = me.last_name || '';

          const params = new URLSearchParams();
          if (firstName) params.append('firstname', firstName);
          if (lastName) params.append('lastname', lastName);
          if (email) params.append('email', email);

          const queryString = params.toString();
          if (queryString) finalUrl = `${baseUrl}&${queryString}`;
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('Error processing user data from store', e);
        }
      }

      const hubspotDiv = document.createElement('div');
      hubspotDiv.className = 'meetings-iframe-container';
      hubspotDiv.dataset.src = finalUrl;
      container.appendChild(hubspotDiv);

      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = 'https://static.hsappstatic.net/MeetingsEmbed/ex/MeetingsEmbedCode.js';
      container.appendChild(script);
    },
    clearHubspotEmbed() {
      const container = this.$refs.hubspotContainer;
      if (container) container.innerHTML = '';
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/vuexy/_colorsVariables.scss';

/* ────────────────────────────────────────────────────────────────
   Labels de formulario
   WHY: 11.9px + opacidad 0.6 hacía los labels casi invisibles. Subimos
   a 16px (de 14px) por feedback del cliente para mejorar legibilidad.
   ──────────────────────────────────────────────────────────────── */
.label-vs {
  font-size: 16px !important;
  font-weight: 500;
  letter-spacing: 0.1px;
  color: rgba(0, 0, 0, 0.78);
  margin-bottom: 8px;
  transition: color 0.18s ease;
}
.has-error .label-vs {
  color: $danger;
}

/* Espaciado vertical entre grupos label+input. */
.vx-col + .vx-col > .label-vs {
  margin-top: 12px;
}

.questions-select .vs__dropdown-menu {
  max-height: 300px !important;
}

/* Inputs / vue-select más altos y con texto más grande.
   WHY: subimos toda la tipografía y altura ~15% para mejorar lectura. */
.select-large {
  ::v-deep .vs__dropdown-toggle {
    min-height: 46px;
    padding: 4px 0 6px 0 !important;
  }
  ::v-deep .vs__selected,
  ::v-deep .vs__search,
  ::v-deep .vs__search::placeholder {
    font-size: 1rem !important;
    line-height: 1.4;
  }
}

/* Opciones del dropdown (lista desplegada). */
::v-deep .vs__dropdown-menu {
  li {
    font-size: 1rem;
    padding: 10px 14px;
  }
}

/* WHY: era `height: 500px` (fijo), lo que impedía que la columna izquierda
   se estirara al alto de la columna derecha cuando esta crecía → quedaba un
   borde blanco abajo. Pasar a `min-height` permite stretch sin perder el
   mínimo cuando la columna derecha es corta. */
.min-h-login {
  min-height: 500px;
}

.questions-illustration {
  width: 86%;
  max-width: 560px;
  height: auto;
}

/* ────────────────────────────────────────────────────────────────
   Encabezado del paso
   ──────────────────────────────────────────────────────────────── */
::v-deep h2 {
  font-size: 1.85rem;
  line-height: 1.25;
}
.step-subtitle {
  font-size: 1.1rem;
  font-weight: 500;
  color: $primary;
  margin-bottom: 3px;
}
.step-meta {
  font-size: 0.85rem;
  color: rgba(0, 0, 0, 0.5);
  margin-bottom: 18px;
}

/* ────────────────────────────────────────────────────────────────
   Stepper visual (chips numeradas) — animación coreografiada
   WHY: cambiar el color del chip y la barra en seco se siente abrupto.
   En vez de eso, la barra se "llena" de izquierda a derecha (gesto
   de avance), y luego el chip siguiente hace un pop al recibirla.
   ──────────────────────────────────────────────────────────────── */
.stepper {
  display: flex;
  align-items: center;
  width: 100%;
  margin: 4px 0 22px;
}
.stepper-segment {
  display: flex;
  align-items: center;
  flex: 1;

  &:last-child { flex: 0 0 auto; }
}
.stepper-chip {
  position: relative;
  flex: 0 0 auto;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9rem;
  font-weight: 600;
  background: #fff;
  color: rgba(0, 0, 0, 0.4);
  border: 2px solid rgba(0, 0, 0, 0.12);
  cursor: default;
  user-select: none;
  /* WHY: la transición del fondo va con delay para esperar a que la barra
     termine de llenarse → el chip "recibe" el progreso visualmente. */
  transition:
    background-color 0.3s ease 0.35s,
    color 0.3s ease 0.35s,
    border-color 0.3s ease 0.35s,
    transform 0.25s cubic-bezier(0.68, -0.55, 0.27, 1.55);

  /* Halo pulsante en el step actual: respira sutilmente. */
  &.current {
    background: $primary;
    color: #fff;
    border-color: $primary;
    transition:
      background-color 0.3s ease,
      color 0.3s ease,
      border-color 0.3s ease,
      transform 0.4s cubic-bezier(0.68, -0.55, 0.27, 1.55) 0.35s;
    /* Pop al volverse current — entra desde scale 1.18 → 1. */
    animation: chipPop 0.5s cubic-bezier(0.68, -0.55, 0.27, 1.55) 0.35s both,
               chipHalo 2.2s ease-in-out 0.85s infinite;
  }

  &.done {
    background: $primary;
    color: #fff;
    border-color: $primary;
    cursor: pointer;

    &:hover { transform: scale(1.08); }

    /* El checkmark hace un pequeño rebote al aparecer. */
    ::v-deep svg {
      animation: checkPop 0.42s cubic-bezier(0.68, -0.55, 0.27, 1.55) both;
    }
  }
}

.stepper-bar {
  flex: 1;
  height: 4px;
  background: rgba(0, 0, 0, 0.08);
  margin: 0 6px;
  border-radius: 3px;
  position: relative;
  overflow: hidden;

  /* La "llenada" es un pseudo-elemento que crece de width:0 a width:100%.
     Animar width es más expresivo que cambiar el background completo. */
  &::after {
    content: '';
    position: absolute;
    inset: 0 auto 0 0;
    width: 0;
    background: linear-gradient(90deg, $primary 0%, lighten($primary, 8%) 100%);
    border-radius: 3px;
    transition: width 0.55s cubic-bezier(0.65, 0, 0.35, 1);
  }
  &.done::after { width: 100%; }
}

@keyframes chipPop {
  0%   { transform: scale(0.7); }
  60%  { transform: scale(1.18); }
  100% { transform: scale(1); }
}
@keyframes chipHalo {
  0%, 100% { box-shadow: 0 0 0 4px rgba(115, 103, 240, 0.16); }
  50%      { box-shadow: 0 0 0 8px rgba(115, 103, 240, 0.06); }
}
@keyframes checkPop {
  0%   { transform: scale(0) rotate(-25deg); opacity: 0; }
  60%  { transform: scale(1.25) rotate(8deg);  opacity: 1; }
  100% { transform: scale(1) rotate(0);        opacity: 1; }
}

/* ────────────────────────────────────────────────────────────────
   Transiciones de contenido entre pasos
   forward: el paso saliente se va a la izquierda; el entrante viene
            desde la derecha. Gesto de "avance".
   backward: invertido.
   ──────────────────────────────────────────────────────────────── */
.step-slide-enter-active,
.step-slide-leave-active,
.step-slide-back-enter-active,
.step-slide-back-leave-active {
  transition: opacity 0.32s ease, transform 0.42s cubic-bezier(0.22, 1, 0.36, 1);
}
.step-slide-enter      { opacity: 0; transform: translateX(28px); }
.step-slide-leave-to   { opacity: 0; transform: translateX(-28px); }
.step-slide-back-enter    { opacity: 0; transform: translateX(-28px); }
.step-slide-back-leave-to { opacity: 0; transform: translateX(28px); }

/* Textarea (use_case) — match the rest of the form.
   WHY: el browser aplica monospace por default a <textarea>; sin esto la
   tipografía del SMIQ rompe la consistencia visual con los <v-select>. */
textarea.vs-input--input {
  font-family: inherit;
  font-size: 1rem;
  line-height: 1.45;
  padding: 10px 12px;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 5px;
  resize: vertical;
  min-height: 92px;
  width: 100%;
  color: rgba(0, 0, 0, 0.78);
  background: $white;
}
textarea.vs-input--input:focus {
  outline: none;
  border-color: $primary;
}
textarea.vs-input--input::placeholder {
  font-size: 1rem;
  color: rgba(0, 0, 0, 0.4);
}

/* Multi-select de "Servicios de interés".
   WHY: solo este v-select usa modo multiple, así que es el único que
   necesita estilo de pill. No tocamos los demás v-select para no romper
   el override global de _vueSelect.scss. */
.services-multiselect {
  ::v-deep .vs__selected {
    background: $primary;
    color: $white;
    border: 1px solid $primary;
    border-radius: 4px;
    padding: 3px 10px;
    margin: 4px 5px 4px 0;
    font-size: 0.92rem;
    font-weight: 500;

    .vs__deselect {
      margin-left: 6px;
      opacity: 0.85;

      svg {
        stroke: $white !important;
        opacity: 1 !important;
      }

      &:hover { opacity: 1; }
    }
  }

  /* Opción bajo el cursor: fondo primary sólido (alineado con la idea ya
     comentada en _vueSelect.scss que usa rgba(var(--vs-primary),1)). */
  ::v-deep .vs__dropdown-option--highlight {
    background: $primary;
    color: $white;
  }

  ::v-deep .vs__no-options {
    padding: 12px;
    text-align: center;
    color: rgba(0, 0, 0, 0.55);
  }
}

/* ────────────────────────────────────────────────────────────────
   Validation states
   ──────────────────────────────────────────────────────────────── */
.error-text {
  display: block;
  margin-top: 4px;
  font-size: 0.85rem;
  color: $danger;
  animation: shake 0.32s ease;
}

.has-error textarea {
  border: 1px solid $danger !important;
}

/* ::v-deep porque .vs__dropdown-toggle vive dentro del componente vue-select
   (scoped CSS no lo alcanza). */
.has-error {
  ::v-deep .vs__dropdown-toggle {
    border: 1px solid $danger !important;
    animation: shake 0.32s ease;
  }
  ::v-deep .vs__dropdown-toggle:focus,
  ::v-deep .vs__dropdown-toggle:focus-within {
    border-color: $danger !important;
    box-shadow: 0 0 0 2px rgba(234, 84, 85, 0.2);
  }
}

@keyframes shake {
  0%, 100% { transform: translateX(0); }
  20%      { transform: translateX(-4px); }
  40%      { transform: translateX(4px); }
  60%      { transform: translateX(-3px); }
  80%      { transform: translateX(2px); }
}

/* ────────────────────────────────────────────────────────────────
   Contador de caracteres (textarea use_case)
   ──────────────────────────────────────────────────────────────── */
.char-counter {
  font-size: 0.8rem;
  color: rgba(0, 0, 0, 0.45);
  font-variant-numeric: tabular-nums;
  margin-left: auto;
  white-space: nowrap;

  &--warn { color: $warning; font-weight: 500; }
}

/* ────────────────────────────────────────────────────────────────
   Botones de navegación
   ──────────────────────────────────────────────────────────────── */
.nav-buttons {
  gap: 12px;

  ::v-deep .vs-button {
    transition: transform 0.15s ease, box-shadow 0.2s ease;

    &:hover:not(.disabled) {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(115, 103, 240, 0.2);
    }
  }
}
</style>
